import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import {
  Box,
  Typography,
  List,
  ListItem,
  Avatar,
  IconButton,
  Chip,
  Button,
  Menu,
  MenuItem,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import {
  PersonAdd,
  GroupAdd,
  Explore,
  CheckCircle,
  Delete,
  MoreVert,
  Info,
  Announcement,
  Settings as SettingsIcon,
  Check,
  Close,
  Block as BlockIcon,
  GroupAdd as GroupAddIcon,
  NotificationsNone,
  AdminPanelSettings,
  DoneAll,
  DeleteSweep,
  PlayCircle,
} from '@mui/icons-material'
import { getUserNotices, markNoticeAsRead, deleteNotice } from '@api/endpoints/notices'
import { NoticeTypeIds, NoticeDataTypes, type UserNotice } from '@api/types/notices'
import { executeNoticeAction, getActionButtonVariant, getActionButtonColor } from '@api/services/noticeActions'
import { profileApi } from '@api/endpoints/profile'
import { isToday, isYesterday } from 'date-fns'

type NotificationCategory = 'all' | 'social' | 'adventures' | 'rewards' | 'system'

const CATEGORY_FILTERS: { key: NotificationCategory; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'social', label: 'Social' },
  { key: 'adventures', label: 'Adventures' },
  { key: 'rewards', label: 'Rewards' },
  { key: 'system', label: 'System' },
]

// Map notice types to categories
function getNotificationCategory(notification: UserNotice): NotificationCategory {
  const typeUpper = notification.type.toUpperCase()

  if (
    typeUpper === NoticeTypeIds.FRIEND_REQUEST.toUpperCase() ||
    typeUpper === NoticeTypeIds.FRIEND_REQUEST_ACCEPTED.toUpperCase() ||
    typeUpper === NoticeTypeIds.GROUP_INVITATION.toUpperCase() ||
    notification.parsedData?.type === NoticeDataTypes.GROUP_JOIN_REQUEST
  ) {
    return 'social'
  }

  if (
    typeUpper === NoticeTypeIds.ADVENTURE_INVITATION.toUpperCase() ||
    typeUpper === NoticeTypeIds.MODERATOR_APPROVAL_REQUEST.toUpperCase() ||
    typeUpper === NoticeTypeIds.SESSION_INVITE.toUpperCase()
  ) {
    return 'adventures'
  }

  if (
    typeUpper === NoticeTypeIds.SYSTEM.toUpperCase() ||
    typeUpper === NoticeTypeIds.GENERAL.toUpperCase()
  ) {
    return 'system'
  }

  return 'system'
}

// Get category color for icon circle
function getCategoryColor(notification: UserNotice, theme: ReturnType<typeof useTheme>): string {
  const typeUpper = notification.type.toUpperCase()

  if (typeUpper === NoticeTypeIds.FRIEND_REQUEST.toUpperCase()) return theme.palette.primary.main
  if (typeUpper === NoticeTypeIds.FRIEND_REQUEST_ACCEPTED.toUpperCase()) return theme.palette.success.main
  if (typeUpper === NoticeTypeIds.GROUP_INVITATION.toUpperCase()) return theme.palette.info.main
  if (notification.parsedData?.type === NoticeDataTypes.GROUP_JOIN_REQUEST) return theme.palette.info.main
  if (typeUpper === NoticeTypeIds.ADVENTURE_INVITATION.toUpperCase()) return theme.palette.warning.main
  if (typeUpper === NoticeTypeIds.SESSION_INVITE.toUpperCase()) return theme.palette.warning.main
  if (typeUpper === NoticeTypeIds.MODERATOR_APPROVAL_REQUEST.toUpperCase()) return theme.palette.secondary.main
  if (typeUpper === NoticeTypeIds.SYSTEM.toUpperCase()) return theme.palette.error.main
  return theme.palette.info.main
}

// Group notifications by time
function groupByTime(notifications: UserNotice[]): { label: string; items: UserNotice[] }[] {
  const groups: { label: string; items: UserNotice[] }[] = []
  const unread: UserNotice[] = []
  const today: UserNotice[] = []
  const yesterday: UserNotice[] = []
  const older: UserNotice[] = []

  for (const n of notifications) {
    if (!n.read) {
      unread.push(n)
      continue
    }
    const date = new Date(n.timestamp || n.created)
    if (isToday(date)) today.push(n)
    else if (isYesterday(date)) yesterday.push(n)
    else older.push(n)
  }

  if (unread.length > 0) groups.push({ label: 'New', items: unread })
  if (today.length > 0) groups.push({ label: 'Earlier Today', items: today })
  if (yesterday.length > 0) groups.push({ label: 'Yesterday', items: yesterday })
  if (older.length > 0) groups.push({ label: 'Older', items: older })

  return groups
}

export default function NotificationsPage() {
  const theme = useTheme()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [category, setCategory] = useState<NotificationCategory>('all')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedNotification, setSelectedNotification] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [resolvedUserInfo, setResolvedUserInfo] = useState<Map<string, { userName: string; avatarUrl: string | null }>>(new Map())

  const {
    data: notificationsData = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['userNotices'],
    queryFn: getUserNotices,
  })

  const notifications = Array.isArray(notificationsData) ? notificationsData : []
  const unreadCount = notifications.filter((n) => !n.read).length

  // Fetch user info for GroupJoinRequest notices
  useEffect(() => {
    const fetchUserInfo = async () => {
      const groupJoinRequests = notifications.filter(
        (n) => n.parsedData?.type === 'GroupJoinRequest' && n.userId && !resolvedUserInfo.has(n.userId)
      )
      if (groupJoinRequests.length === 0) return

      const newUserInfo = new Map(resolvedUserInfo)
      for (const notice of groupJoinRequests) {
        try {
          const profile = await profileApi.getUserProfile(notice.userId)
          newUserInfo.set(notice.userId, {
            userName: profile.displayName || profile.userName || 'Unknown User',
            avatarUrl: profile.avatarUrl || null,
          })
        } catch {
          newUserInfo.set(notice.userId, { userName: 'Unknown User', avatarUrl: null })
        }
      }
      setResolvedUserInfo(newUserInfo)
    }
    fetchUserInfo()
  }, [notifications])

  const markAsReadMutation = useMutation({
    mutationFn: markNoticeAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userNotices'] })
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
    },
  })

  const deleteNoticeMutation = useMutation({
    mutationFn: deleteNotice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userNotices'] })
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
    },
  })

  const isGroupJoinRequest = (notification: UserNotice): boolean => {
    return notification.parsedData?.type === NoticeDataTypes.GROUP_JOIN_REQUEST
  }

  const getIconForType = (notification: UserNotice): React.ReactNode => {
    if (notification.iconUrl) {
      return <Avatar src={notification.iconUrl} sx={{ width: 24, height: 24 }} />
    }
    if (isGroupJoinRequest(notification)) {
      const userInfo = resolvedUserInfo.get(notification.userId)
      if (userInfo?.avatarUrl) {
        return <Avatar src={userInfo.avatarUrl} sx={{ width: 24, height: 24 }} />
      }
      return <GroupAddIcon sx={{ fontSize: 22 }} />
    }

    const typeUpper = notification.type.toUpperCase()
    switch (typeUpper) {
      case NoticeTypeIds.FRIEND_REQUEST.toUpperCase(): return <PersonAdd sx={{ fontSize: 22 }} />
      case NoticeTypeIds.FRIEND_REQUEST_ACCEPTED.toUpperCase(): return <CheckCircle sx={{ fontSize: 22 }} />
      case NoticeTypeIds.GROUP_INVITATION.toUpperCase(): return <GroupAdd sx={{ fontSize: 22 }} />
      case NoticeTypeIds.ADVENTURE_INVITATION.toUpperCase(): return <Explore sx={{ fontSize: 22 }} />
      case NoticeTypeIds.SESSION_INVITE.toUpperCase(): return <PlayCircle sx={{ fontSize: 22 }} />
      case NoticeTypeIds.MODERATOR_APPROVAL_REQUEST.toUpperCase(): return <AdminPanelSettings sx={{ fontSize: 22 }} />
      case NoticeTypeIds.SYSTEM.toUpperCase(): return <SettingsIcon sx={{ fontSize: 22 }} />
      case NoticeTypeIds.GENERAL.toUpperCase(): return <Announcement sx={{ fontSize: 22 }} />
      default: return <Info sx={{ fontSize: 22 }} />
    }
  }

  const getActionIcon = (actionType: string): React.ReactNode => {
    if (actionType.includes('accept') || actionType.includes('join')) return <Check />
    if (actionType.includes('block')) return <BlockIcon />
    if (actionType.includes('decline') || actionType.includes('deny')) return <Close />
    return null
  }

  const handleNoticeAction = async (
    noticeId: string,
    noticeType: string,
    actionType: string,
    actionData: string,
    parsedData?: any
  ) => {
    setActionLoading(noticeId)
    try {
      await executeNoticeAction(noticeType, actionType, actionData, parsedData)
      await deleteNotice(noticeId)
      queryClient.invalidateQueries({ queryKey: ['userNotices'] })
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] })

      // Navigate to session lobby after accepting a session invite
      if (actionType === 'accept_session_invite') {
        const adventureId = parsedData?.AdventureId
        const sessionId = parsedData?.SessionId || actionData
        if (adventureId && sessionId) {
          navigate(`/adventures/${adventureId}`, { state: { sessionId } })
        }
      }
    } catch (error) {
      console.error('Failed to execute notice action:', error)
    } finally {
      setActionLoading(null)
    }
  }

  const isSessionInvite = (notification: UserNotice): boolean => {
    return notification.type.toUpperCase() === NoticeTypeIds.SESSION_INVITE.toUpperCase()
  }

  const handleNotificationClick = (notification: UserNotice) => {
    if (notification.type.toUpperCase() === NoticeTypeIds.MODERATOR_APPROVAL_REQUEST.toUpperCase()) {
      if (!notification.read) markAsReadMutation.mutate(notification.id)
      navigate('/moderator-approvals')
      return
    }
    if (isGroupJoinRequest(notification)) {
      const groupId = notification.parsedData?.relatedId
      if (groupId) {
        if (!notification.read) markAsReadMutation.mutate(notification.id)
        navigate(`/groups/${groupId}?tab=requests`)
      }
      return
    }
    if (isSessionInvite(notification)) {
      const adventureId = notification.parsedData?.AdventureId
      if (adventureId) {
        if (!notification.read) markAsReadMutation.mutate(notification.id)
        navigate(`/adventures/${adventureId}`)
      }
    }
  }

  const getTimeAgo = (timestamp: string) => {
    const time = new Date(timestamp)
    // Handle default/invalid dates from backend (e.g. "0001-01-01T00:00:00")
    if (isNaN(time.getTime()) || time.getFullYear() < 2000) return ''
    const now = new Date()
    const diffMs = now.getTime() - time.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return time.toLocaleDateString()
  }

  const markAllAsRead = async () => {
    const unreadNotices = notifications.filter((n) => !n.read)
    await Promise.all(unreadNotices.map((n) => markNoticeAsRead(n.id)))
    queryClient.invalidateQueries({ queryKey: ['userNotices'] })
    queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
  }

  const clearAll = async () => {
    await Promise.all(notifications.map((n) => deleteNotice(n.id)))
    queryClient.invalidateQueries({ queryKey: ['userNotices'] })
    queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
  }

  // Filter by category
  const categoryFiltered = category === 'all'
    ? notifications
    : notifications.filter((n) => getNotificationCategory(n) === category)

  const timeGroups = groupByTime(categoryFiltered)

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {isError && (
        <Alert severity="error" sx={{ m: 2 }}>
          Failed to load notifications: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      )}

      {/* Header with bulk actions */}
      <Box sx={{ px: 2, pt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip label={`${unreadCount} unread`} color="primary" size="small" />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {unreadCount > 0 && (
            <Button variant="outlined" size="small" startIcon={<DoneAll />} onClick={markAllAsRead}>
              Mark All Read
            </Button>
          )}
          {notifications.length > 0 && (
            <Button variant="outlined" size="small" color="error" startIcon={<DeleteSweep />} onClick={clearAll}>
              Clear All
            </Button>
          )}
        </Box>
      </Box>

      {/* Category Filter Chips */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {CATEGORY_FILTERS.map((filter) => (
          <Chip
            key={filter.key}
            label={filter.label}
            size="small"
            onClick={() => setCategory(filter.key)}
            sx={{
              fontWeight: 600,
              ...(category === filter.key
                ? {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }
                : {
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                    '&:hover': { bgcolor: 'action.selected' },
                  }),
            }}
          />
        ))}
      </Box>

      {/* Notifications List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {timeGroups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <NotificationsNone sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
            <Typography variant="body2">
              {category === 'rewards' ? 'No rewards yet' : 'No notifications'}
            </Typography>
          </Box>
        ) : (
          timeGroups.map((group) => (
            <Box key={group.label}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ px: 2, py: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {group.label}
              </Typography>
              <List disablePadding>
                {group.items.map((notification, index) => {
                  const categoryColor = getCategoryColor(notification, theme)
                  return (
                    <Box key={notification.id}>
                      <ListItem
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                          px: 2,
                          py: 1.5,
                          cursor: isGroupJoinRequest(notification) || isSessionInvite(notification) || notification.type.toUpperCase() === NoticeTypeIds.MODERATOR_APPROVAL_REQUEST.toUpperCase() ? 'pointer' : 'default',
                          '&:hover': { bgcolor: 'action.hover' },
                          gap: 1.5,
                          flexDirection: 'column',
                          alignItems: 'stretch',
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                          {/* Category-colored icon circle with unread dot */}
                          <Box sx={{ position: 'relative', flexShrink: 0 }}>
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                bgcolor: alpha(categoryColor, 0.15),
                                color: categoryColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {getIconForType(notification)}
                            </Box>
                            {!notification.read && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: 'primary.main',
                                  border: '2px solid',
                                  borderColor: 'background.paper',
                                }}
                              />
                            )}
                          </Box>

                          {/* Content */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {isSessionInvite(notification) && notification.parsedData ? (
                              <>
                                {notification.parsedData.AdventureId ? (
                                  <Typography
                                    component={Link}
                                    to={`/adventures/${notification.parsedData.AdventureId}`}
                                    variant="body2"
                                    fontWeight={notification.read ? 400 : 600}
                                    noWrap
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                      mb: 0.25,
                                      display: 'block',
                                      color: 'text.primary',
                                      textDecoration: 'none',
                                      '&:hover': { textDecoration: 'underline', color: 'primary.main' },
                                    }}
                                  >
                                    {notification.parsedData.AdventureName || 'Session Invite'}
                                  </Typography>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    fontWeight={notification.read ? 400 : 600}
                                    noWrap
                                    sx={{ mb: 0.25 }}
                                  >
                                    {notification.parsedData.AdventureName || 'Session Invite'}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" noWrap component="div">
                                  {notification.parsedData.InvitedBy ? (
                                    <Link
                                      to={`/profile/${notification.parsedData.InvitedBy}`}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ color: 'inherit', textDecoration: 'none' }}
                                      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline' }}
                                      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none' }}
                                    >
                                      {notification.parsedData.InviterName || 'Someone'}
                                    </Link>
                                  ) : (
                                    notification.parsedData.InviterName || 'Someone'
                                  )}
                                  {' invited you'}
                                  {notification.parsedData.SessionName ? ` · ${notification.parsedData.SessionName}` : ''}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography
                                  variant="body2"
                                  fontWeight={notification.read ? 400 : 600}
                                  noWrap
                                  sx={{ mb: 0.25 }}
                                >
                                  {notification.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap component="div">
                                  {isGroupJoinRequest(notification) && resolvedUserInfo.has(notification.userId)
                                    ? notification.message?.replace(/^user\d+/, resolvedUserInfo.get(notification.userId)!.userName)
                                    : notification.message}
                                </Typography>
                                {getTimeAgo(notification.timestamp || notification.created) && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                                    {getTimeAgo(notification.timestamp || notification.created)}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>

                          {/* Session invite inline action buttons */}
                          {isSessionInvite(notification) && notification.actions && notification.actions.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, alignSelf: 'center', flexShrink: 0 }}>
                              {notification.actions.map((action, actionIndex) => (
                                <Button
                                  key={actionIndex}
                                  variant={getActionButtonVariant(action.actionType)}
                                  color={getActionButtonColor(action.actionType)}
                                  size="small"
                                  disabled={actionLoading === notification.id}
                                  startIcon={getActionIcon(action.actionType)}
                                  sx={{
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleNoticeAction(
                                      notification.id,
                                      notification.type,
                                      action.actionType,
                                      action.actionData,
                                      notification.parsedData
                                    )
                                  }}
                                >
                                  {actionLoading === notification.id ? 'Processing...' : action.label}
                                </Button>
                              ))}
                            </Box>
                          )}

                          {/* More menu */}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              setAnchorEl(e.currentTarget)
                              setSelectedNotification(notification.id)
                            }}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Box>

                        {/* Action Buttons (non-session-invite) */}
                        {!isSessionInvite(notification) && notification.actions && notification.actions.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5, ml: 7, flexWrap: 'wrap' }}>
                            {notification.actions.map((action, actionIndex) => (
                              <Button
                                key={actionIndex}
                                variant={getActionButtonVariant(action.actionType)}
                                color={getActionButtonColor(action.actionType)}
                                size="small"
                                disabled={actionLoading === notification.id}
                                startIcon={getActionIcon(action.actionType)}
                                sx={{
                                  minWidth: 'auto',
                                  px: 1.5,
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleNoticeAction(
                                    notification.id,
                                    notification.type,
                                    action.actionType,
                                    action.actionData,
                                    notification.parsedData
                                  )
                                }}
                              >
                                {actionLoading === notification.id ? 'Processing...' : action.label}
                              </Button>
                            ))}
                          </Box>
                        )}
                      </ListItem>
                      {index < group.items.length - 1 && <Divider component="li" />}
                    </Box>
                  )
                })}
              </List>
            </Box>
          ))
        )}
      </Box>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => { setAnchorEl(null); setSelectedNotification(null) }}>
        {selectedNotification &&
          !notifications.find((n) => n.id === selectedNotification)?.read && (
            <MenuItem onClick={() => {
              markAsReadMutation.mutate(selectedNotification!)
              setAnchorEl(null)
              setSelectedNotification(null)
            }}>
              <CheckCircle sx={{ mr: 1 }} fontSize="small" />
              Mark as read
            </MenuItem>
          )}
        <MenuItem
          onClick={() => {
            deleteNoticeMutation.mutate(selectedNotification!)
            setAnchorEl(null)
            setSelectedNotification(null)
          }}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  )
}
